import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  AlarmClock, ArrowRight, Building2, CalendarCheck, Check, Mail, MessageCircle, NotebookPen, Phone, Upload, UserPlus, UsersRound, Video, Workflow, X,
} from 'lucide-react';
import { toast } from 'react-toastify';

import { Skeleton, formatDay, formatNumber, formatTime } from 'src/stage/StageUi';
import { characterFor } from 'src/stage/characters';
import { SnoozeMenu, HelpTip } from './shared/StageKit';
import LogActivityDialog from './shared/LogActivityDialog';
import { QUICK_ADD_EVENT, openLead } from './shared/CommandPalette';
import { ACTIVITY_TYPES, apiMessage, completeActivity, dueBucket, dueLabel, rescheduleActivity, telHref, whatsappHref } from './shared/crmWork';

// AUTO-CRM-32: Home is "Today": the one lead to act on now, the rest of the
// day's promises, and (for new workspaces) a plain-language setup guide.
const TYPE_ICONS = { CALL: Phone, WHATSAPP: MessageCircle, MEETING: Video, EMAIL: Mail, FOLLOWUP: AlarmClock, NOTE: NotebookPen };
const changed = () => window.dispatchEvent(new Event('crm:followups-changed'));

export const isDueToday = (item) => ['overdue', 'today'].includes(dueBucket(item?.scheduled_at));

export function UpNext({ loading, item, fallback, onChanged }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState(null);
  const nila = characterFor('nila');
  // The dialog keeps its own lead: the list refreshes (and this card may switch
  // to the next item or the fallback) while it is still open.
  const dialogEl = dialog && <LogActivityDialog lead={dialog.lead} mode={dialog.mode} pendingFollowUp={dialog.pending} onClose={() => setDialog(null)} onSaved={onChanged} />;
  if (loading && item === undefined) return <section className="ws-spot crm-home-spot"><Skeleton width={120} height={12} style={{ opacity: 0.3 }} /></section>;
  if (!item) return <>{fallback}{dialogEl}</>;
  const lead = item.lead || {};
  const first = String(lead.name || 'them').split(/\s+/)[0];
  const late = dueBucket(item.scheduled_at) === 'overdue';
  const Icon = TYPE_ICONS[item.type] || AlarmClock;
  const tel = telHref(lead.phone);
  const wa = whatsappHref(lead.phone);

  const done = async () => {
    setBusy(true);
    try { await completeActivity(item.id); changed(); toast.success('Done. Plan what’s next?'); setDialog({ mode: 'plan', lead }); onChanged?.(); }
    catch (error) { toast.error(apiMessage(error, 'Couldn’t mark this as done.')); }
    finally { setBusy(false); }
  };
  const snooze = async (when) => {
    setBusy(true);
    try { await rescheduleActivity(item.id, when); changed(); toast.info(`Moved to ${dueLabel(when)}`); onChanged?.(); }
    catch (error) { toast.error(apiMessage(error, 'Couldn’t move this follow-up.')); }
    finally { setBusy(false); }
  };

  return (
    <section className={`ws-spot crm-home-spot crm-upnext ws-rise${late ? ' is-late' : ''}`} aria-label="Up next">
      <div className="ws-spot-art crm-home-spot-halo" aria-hidden="true" />
      {nila?.full && <img className="ws-spot-art crm-home-spot-art" src={nila.full} alt="" aria-hidden="true" />}
      <span className="ws-eyebrow">{late ? 'Overdue' : 'Up next'} · {dueLabel(item.scheduled_at)}</span>
      <div className="crm-upnext-who">
        <span className="crm-upnext-icon" aria-hidden="true"><Icon size={18} /></span>
        <h2 className="crm-home-spot-title crm-upnext-title">{ACTIVITY_TYPES[item.type]?.verb || 'Follow up with'} <button type="button" className="crm-upnext-name" onClick={() => openLead(navigate, lead)} title="Open lead">{lead.name || 'this lead'}</button></h2>
      </div>
      <p className="ws-muted crm-home-spot-text crm-upnext-sub">{item.note || 'No note on this follow-up.'}{lead.stage?.name ? <span className="crm-upnext-stage">{lead.stage.name}</span> : null}</p>
      <div className="crm-home-spot-actions">
        {tel && <a className="ws-btn ws-btn--white ws-btn--sm" href={tel}><Phone size={14} />Call {first}</a>}
        {wa && <a className="ws-btn ws-btn--glass ws-btn--sm" href={wa} target="_blank" rel="noreferrer"><MessageCircle size={14} />WhatsApp</a>}
        <button type="button" className={`ws-btn ${tel ? 'ws-btn--glass' : 'ws-btn--white'} ws-btn--sm`} onClick={() => setDialog({ mode: 'log', lead, pending: item })} disabled={busy}><NotebookPen size={14} />Log what happened</button>
        <button type="button" className="ws-btn ws-btn--glass ws-btn--sm" onClick={done} disabled={busy}><Check size={14} />Done</button>
        <SnoozeMenu onPick={snooze} disabled={busy} buttonClass="ws-btn ws-btn--glass ws-btn--sm" />
      </div>
      {dialogEl}
    </section>
  );
}

function whenCell(value) {
  const bucket = dueBucket(value);
  if (bucket === 'overdue') return { top: 'Late', bottom: formatDay(value), late: true };
  if (bucket === 'nodate') return { top: 'Any time', bottom: 'No date' };
  return { top: formatTime(value), bottom: bucket === 'today' ? 'Today' : formatDay(value) };
}

export function YourDay({ loading, items, skipId, total, onChanged }) {
  const navigate = useNavigate();
  const [busyId, setBusyId] = useState(null);
  const rows = (items || []).filter((item) => item.id !== skipId).slice(0, 6);
  const dueCount = rows.filter(isDueToday).length;
  const more = Math.max(0, (total || 0) - rows.length - (skipId ? 1 : 0));
  const done = async (item) => {
    setBusyId(item.id);
    try { await completeActivity(item.id); changed(); toast.success(`Done: ${item.lead?.name || 'follow-up'}`); onChanged?.(); }
    catch (error) { toast.error(apiMessage(error, 'Couldn’t mark this as done.')); }
    finally { setBusyId(null); }
  };
  const subtitle = !rows.length ? '' : dueCount
    ? `${formatNumber(dueCount)} ${skipId ? 'more ' : ''}due today${dueCount < rows.length ? ', then what’s coming up' : ''}.`
    : `Nothing else due today. Coming up next:`;
  return (
    <section className="ws-card ws-card-pad crm-home-card ws-rise">
      <div className="ws-card-head"><h2 className="ws-h3 ws-grow">Your day</h2><Link className="crm-link" to="/follow-ups">All follow-ups</Link></div>
      {subtitle && <p className="ws-muted" style={{ marginBottom: 4 }}>{subtitle}</p>}
      {loading && !items ? [0, 1, 2].map((key) => <div className="ws-row" key={key}><Skeleton width={46} height={30} /><div className="ws-grow"><Skeleton width="70%" /></div></div>)
        : rows.length === 0 ? (
          <div className="ws-empty crm-home-empty">
            <CalendarCheck size={20} color="#1B7A5A" />
            <strong>{skipId ? 'That’s the only one' : 'Nothing planned'}</strong>
            {skipId ? 'Finish it, then plan follow-ups for your new leads.' : 'Plan a follow-up on each open lead so nobody is forgotten.'}
            <Link className="ws-btn ws-btn--quiet ws-btn--sm" style={{ marginTop: 8 }} to="/follow-ups">Find leads without a next step</Link>
          </div>
        ) : (
          <>
            <ul className="crm-home-list">
              {rows.map((item) => {
                const Icon = TYPE_ICONS[item.type] || AlarmClock;
                const when = whenCell(item.scheduled_at);
                return (
                  <li key={item.id} className="crm-home-day">
                    <button type="button" className="crm-home-queue" onClick={() => openLead(navigate, item.lead)}>
                      <span className="crm-home-when"><strong className={when.late ? 'is-late' : ''}>{when.top}</strong><span>{when.bottom}</span></span>
                      <span className="ws-grow" style={{ minWidth: 0 }}>
                        <span className="crm-home-row-title"><Icon size={13} aria-hidden="true" className="crm-home-type" />{item.lead?.name || 'Lead'}</span>
                        <span className="crm-home-row-sub">{item.note || ACTIVITY_TYPES[item.type]?.label}</span>
                      </span>
                    </button>
                    <button type="button" className="crm-icon-btn crm-icon-btn--sm" onClick={() => done(item)} disabled={busyId === item.id} aria-label={`Mark done: ${item.lead?.name || 'follow-up'}`} title="Mark done"><Check size={14} /></button>
                  </li>
                );
              })}
            </ul>
            {more > 0 && <Link className="crm-home-more" to="/follow-ups">+{formatNumber(more)} more planned<ArrowRight size={13} /></Link>}
          </>
        )}
    </section>
  );
}

const GUIDE_KEY = 'crm.guide.hidden';

export function GettingStarted({ workspaceId, overview, capabilities, followUpTotal }) {
  const navigate = useNavigate();
  const [hidden, setHidden] = useState(() => { try { return localStorage.getItem(`${GUIDE_KEY}:${workspaceId}`) === '1'; } catch { return false; } });
  const [selected, setSelected] = useState(null);
  if (!overview || hidden) return null;
  const setup = overview.setup || {};
  const leadTotal = overview.stats?.leads?.total || 0;
  const steps = [
    { id: 'pipeline', icon: Workflow, title: 'Set up your sales stages', body: 'The steps a lead goes through, from enquiry to won. Start from a ready template.', done: setup.has_pipeline && setup.has_stages, action: capabilities?.can_manage_workflows ? { label: 'Choose a template', run: () => navigate('/admin/pipelines?create=1') } : null },
    { id: 'leads', icon: UserPlus, title: 'Add your leads', body: 'Add a lead by hand or import your spreadsheet. Each one lands in the first stage.', done: leadTotal > 0, action: { label: 'Add a lead', run: () => window.dispatchEvent(new Event(QUICK_ADD_EVENT)) }, extra: { label: 'Import', icon: Upload, run: () => navigate('/crm', { state: { openImport: true } }) } },
    { id: 'followup', icon: CalendarCheck, title: 'Plan your first follow-up', body: 'A call or meeting at a set time. Follow-ups are how nobody gets forgotten.', done: followUpTotal > 0, action: { label: 'Open follow-ups', run: () => navigate('/follow-ups') } },
    { id: 'team', icon: UsersRound, title: 'Invite your team', body: 'Give each lead an owner so everyone knows who calls whom.', done: setup.has_team, action: capabilities?.can_manage_members ? { label: 'Invite people', run: () => navigate('/teams') } : null },
    { id: 'profile', icon: Building2, title: 'Complete your business profile', body: 'Your name, phone and website appear in emails and messages.', done: (setup.profile_completion_percent || 0) >= 100, action: capabilities?.can_update_workspace ? { label: 'Open profile', run: () => navigate('/settings/workspace') } : null },
  ];
  const doneCount = steps.filter((step) => step.done).length;
  if (doneCount === steps.length) return null;
  const firstOpen = steps.find((step) => !step.done);
  const current = steps.find((step) => step.id === selected) || firstOpen;
  const hide = () => { setHidden(true); try { localStorage.setItem(`${GUIDE_KEY}:${workspaceId}`, '1'); } catch { /* private mode */ } };

  return (
    <section className="ws-card crm-guide ws-rise" aria-label="Get your CRM ready">
      <header className="crm-guide-head">
        <span className="crm-home-setup-ring" style={{ '--done': `${(doneCount / steps.length) * 360}deg` }} aria-hidden="true"><b>{doneCount}/{steps.length}</b></span>
        <div className="ws-grow">
          <h2 className="ws-h3">Get your CRM ready</h2>
          <p className="ws-muted">Every <HelpTip term="lead">lead</HelpTip> moves through your pipeline’s <HelpTip term="stage">stages</HelpTip> until it’s won or lost. <HelpTip term="followup">Follow-ups</HelpTip> make sure nobody is forgotten.</p>
        </div>
        <button type="button" className="crm-icon-btn crm-icon-btn--sm" onClick={hide} aria-label="Hide the setup guide" title="Hide for now"><X size={14} /></button>
      </header>
      <div className="crm-guide-body">
        <ol className="crm-guide-list">
          {steps.map((step, index) => (
            <li key={step.id}>
              <button type="button" className={`crm-guide-item${step.done ? ' is-done' : ''}${step === current ? ' is-current' : ''}`} onClick={() => setSelected(step.id)} aria-current={step === current ? 'step' : undefined}>
                <span className="crm-guide-num" aria-hidden="true">{step.done ? <Check size={12} strokeWidth={3} /> : index + 1}</span>
                <span className="ws-grow">{step.title}</span>
                {step.done && <span className="crm-guide-tag">Done</span>}
              </button>
            </li>
          ))}
        </ol>
        <div className="crm-guide-detail" key={current.id}>
          <span className={`crm-guide-icon${current.done ? ' is-done' : ''}`} aria-hidden="true"><current.icon size={20} /></span>
          <div className="crm-guide-copy">
            <span className="crm-guide-over">{current.done ? 'Done' : current === firstOpen ? 'Next step' : 'Step'}</span>
            <h3>{current.title}</h3>
            <p>{current.body}</p>
            {!current.done && (current.action || current.extra) && (
              <div className="crm-guide-actions">
                {current.action && <button type="button" className="ws-btn ws-btn--primary ws-btn--sm" onClick={current.action.run}>{current.action.label}<ArrowRight size={13} /></button>}
                {current.extra && <button type="button" className="ws-btn ws-btn--quiet ws-btn--sm" onClick={current.extra.run}><current.extra.icon size={13} />{current.extra.label}</button>}
              </div>
            )}
            {!current.done && !current.action && <p className="crm-guide-ask">Only a workspace admin can do this. Ask yours to finish it.</p>}
          </div>
        </div>
      </div>
    </section>
  );
}
